import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';

import { useLocalStorage } from '@vueuse/core';

type Theme = 'light' | 'dark' | 'auto';

export const useUIStore = defineStore('ui', () => {
  const sidebarCollapsed = useLocalStorage<boolean>('ui_sidebar_collapsed', false);
  const mobileMenuOpen = ref(false);

  const theme = useLocalStorage<Theme>('ui_theme', 'auto');

  const globalLoading = ref(false);
  const loadingMessage = ref<string | null>(null);

  const compactMode = useLocalStorage<boolean>('ui_compact_mode', false);

  const soundEnabled = useLocalStorage<boolean>('ui_sound_enabled', true);

  const systemPrefersDark = ref(window.matchMedia('(prefers-color-scheme: dark)').matches);

  const isDarkMode = computed(() => {
    if (theme.value === 'dark') {
      return true;
    }
    if (theme.value === 'light') {
      return false;
    }

    return systemPrefersDark.value;
  });

  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value;
  }

  function setSidebarCollapsed(collapsed: boolean): void {
    sidebarCollapsed.value = collapsed;
  }

  function toggleMobileMenu(): void {
    mobileMenuOpen.value = !mobileMenuOpen.value;
  }

  function setMobileMenuOpen(open: boolean): void {
    mobileMenuOpen.value = open;
  }

  function closeMobileMenu(): void {
    mobileMenuOpen.value = false;
  }

  function setTheme(newTheme: Theme): void {
    theme.value = newTheme;
    applyTheme();
  }

  function applyTheme(): void {
    const root = document.documentElement;
    root.classList.toggle('dark-mode', isDarkMode.value);
  }

  function showGlobalLoading(message?: string): void {
    globalLoading.value = true;
    loadingMessage.value = message || null;
  }

  function hideGlobalLoading(): void {
    globalLoading.value = false;
    loadingMessage.value = null;
  }

  function setCompactMode(compact: boolean): void {
    compactMode.value = compact;
  }

  function toggleCompactMode(): void {
    compactMode.value = !compactMode.value;
  }

  function setSoundEnabled(enabled: boolean): void {
    soundEnabled.value = enabled;
  }

  function toggleSound(): void {
    soundEnabled.value = !soundEnabled.value;
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', e => {
    systemPrefersDark.value = e.matches;
  });

  watch(isDarkMode, () => {
    applyTheme();
  });

  applyTheme();

  return {
    sidebarCollapsed,
    mobileMenuOpen,
    theme,
    globalLoading,
    loadingMessage,
    compactMode,
    soundEnabled,

    isDarkMode,

    toggleSidebar,
    setSidebarCollapsed,
    toggleMobileMenu,
    setMobileMenuOpen,
    closeMobileMenu,
    setTheme,
    applyTheme,
    showGlobalLoading,
    hideGlobalLoading,
    setCompactMode,
    toggleCompactMode,
    setSoundEnabled,
    toggleSound,
  };
});
